/**
 * Foundation/Core — Suite Footer
 *
 * Footer canonico della Gruppo Impresa Suite. Nessun riferimento a Product Pack
 * specifici (GI Presenze, GI Studio, ...). Il componente e' parametrizzato via prop.
 *
 * PROP
 * - `productName` (opzionale): nome del Product Pack corrente (es. "Gestionale Presenze",
 *   "Studio Legale", "Fatturazione"). Se assente, il pipe con il nome viene omesso.
 *   Il chiamante (wrapper Product) e' la Single Source of Truth per questo valore.
 *
 * SSoT
 * - Versione applicazione e data ultimo aggiornamento arrivano ESCLUSIVAMENTE da
 *   `GET /api/system/version` (backend Foundation, che legge `/app/VERSION` e
 *   `/app/RELEASE_DATE`). Nessun valore hardcoded: in offline si mostra "—" per
 *   evitare valori stale (Consolidamento Foundation v1.3.0, Punto 1 — Msg 269).
 * - Cache session-scoped: `sessionStorage["app_version_cache"]` (chiave stabile Suite).
 *
 * COPYRIGHT / LEGAL
 * - "Gruppo Impresa STP a r.l." e la P.IVA sono attributi dell'AZIENDA che vende
 *   l'intera Suite (identici per tutti i Product Pack) → CORE.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/foundation/api/client";
import { formatDateIT } from "@/foundation/formatting/dates";
import { useAuth } from "@/foundation/auth/AuthContext";
import { SystemInfoDialog } from "./SystemInfoDialog";

const SYSTEM_INFO_ROLES = ["admin", "super_admin", "studio_operator"];

export function SuiteFooter({ productName }) {
  const [appVersion, setAppVersion] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const { user } = useAuth() || {};
  const canSeeSystemInfo = SYSTEM_INFO_ROLES.includes(user?.role);

  useEffect(() => {
    let alive = true;
    try {
      const cached = sessionStorage.getItem("app_version_cache");
      if (cached) setAppVersion(cached);
    } catch (_) { /* ignore */ }
    (async () => {
      try {
        const { data } = await api.get("/system/version");
        if (alive && data?.version) {
          setAppVersion(data.version);
          if (data?.updated_at) setReleaseDate(data.updated_at);
          try { sessionStorage.setItem("app_version_cache", data.version); } catch (_) { /* ignore */ }
        }
      } catch (_) { /* fallback */ }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <footer
      className="h-8 w-full shrink-0 overflow-x-auto border-t border-suite-neutral-200 bg-white px-4 text-[10px] text-suite-neutral-500"
      data-testid="app-footer"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div className="mx-auto flex h-full min-w-max items-center justify-between gap-8 whitespace-nowrap">
        <div className="flex items-center gap-x-2">
          <span className="font-semibold text-suite-neutral-700">Gruppo Impresa Suite</span>
          {productName && (
            <>
              <span className="text-suite-neutral-400">·</span>
              <span>{productName}</span>
            </>
          )}
          <span className="text-suite-neutral-400">·</span>
          {canSeeSystemInfo ? (
            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              className="font-mono tabular-nums underline-offset-2 transition-colors hover:text-suite-neutral-800 hover:underline"
              title="Informazioni di sistema"
              data-testid="footer-app-version"
            >
              v{appVersion || "—"}
            </button>
          ) : (
            <span className="font-mono tabular-nums" data-testid="footer-app-version">v{appVersion || "—"}</span>
          )}
          <span className="text-suite-neutral-400">·</span>
          <span>Ultimo aggiornamento {releaseDate ? formatDateIT(releaseDate) : "—"}</span>
        </div>
        <div className="flex items-center gap-x-2" data-testid="footer-legal">
          <span>© 2026</span>
          <span className="text-suite-neutral-400">·</span>
          <span data-testid="footer-managed-by">Gruppo Impresa STP a r.l.</span>
          <span className="text-suite-neutral-400">·</span>
          <span>C.F./P.IVA 15948751001</span>
          <span className="text-suite-neutral-400">·</span>
          <span>Tutti i diritti riservati.</span>
        </div>
      </div>
      {canSeeSystemInfo && <SystemInfoDialog open={infoOpen} onOpenChange={setInfoOpen} />}
    </footer>
  );
}

export default SuiteFooter;
